import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout/Layout';
import { chatAPI } from '../services/api';
import './Chat.css';

const Chat = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingConversation, setEditingConversation] = useState(null);
  const [formData, setFormData] = useState({
    userId: 1,
    title: '',
    language: 'en'
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    hasMore: false
  });

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async (page = 1) => {
    try {
      setLoading(true);
      const response = await chatAPI.getConversations(1, page, 20);
      setConversations(response.data.conversations);
      setPagination(response.data.pagination);
    } catch (error) {
      console.error('Failed to fetch conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingConversation) {
        await chatAPI.updateConversation(editingConversation.id, formData);
        alert('Conversation updated successfully!');
      } else {
        await chatAPI.createConversation(formData);
        alert('Conversation created successfully!');
      }
      
      setShowModal(false);
      setEditingConversation(null);
      resetForm();
      fetchConversations();
    } catch (error) {
      console.error('Failed to save conversation:', error);
      alert('Failed to save conversation. Please try again.');
    }
  };

  const handleEdit = (conversation) => {
    setEditingConversation(conversation);
    setFormData({
      userId: conversation.user_id,
      title: conversation.title,
      language: conversation.language
    });
    setShowModal(true);
  };

  const handleDelete = async (conversationId) => {
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      try {
        await chatAPI.deleteConversation(conversationId, 1);
        alert('Conversation deleted successfully!');
        fetchConversations();
      } catch (error) {
        console.error('Failed to delete conversation:', error);
        alert('Failed to delete conversation. Please try again.');
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const resetForm = () => {
    setFormData({
      userId: 1,
      title: '',
      language: 'en'
    });
  };

  const handleCancel = () => {
    setShowModal(false);
    setEditingConversation(null);
    resetForm();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Layout title="Chat Management">
        <div className="loading">Loading conversations...</div>
      </Layout>
    );
  }

  return (
    <Layout title="Chat Management">
      <div className="chat-management">
        <div className="page-header">
          <h2>Chat Management</h2>
          <button 
            className="create-btn"
            onClick={() => setShowModal(true)}
          >
            + Create Conversation
          </button>
        </div>

        <div className="conversations-table-container">
          <table className="conversations-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>User ID</th>
                <th>Title</th>
                <th>Language</th>
                <th>Messages</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {conversations.length === 0 ? (
                <tr>
                  <td colSpan="8" className="no-data">No conversations found</td>
                </tr>
              ) : (
                conversations.map((conversation) => (
                  <tr key={conversation.id}>
                    <td>{conversation.id}</td>
                    <td>
                      <span className="user-id-badge">
                        {conversation.user_id}
                      </span>
                    </td>
                    <td className="title-cell">
                      <div className="conversation-title">
                        {conversation.title}
                      </div>
                    </td>
                    <td>
                      <span className="language-badge">
                        {conversation.language.toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className="message-count-badge">
                        {conversation.message_count || 0}
                      </span>
                    </td>
                    <td>{formatDate(conversation.created_at)}</td>
                    <td>{formatDate(conversation.updated_at)}</td>
                    <td>
                      <div className="action-buttons">
                        <button 
                          className="edit-btn"
                          onClick={() => handleEdit(conversation)}
                          title="Edit Conversation"
                        >
                          ✏️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDelete(conversation.id)}
                          title="Delete Conversation"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination-info">
          Showing {conversations.length} of {pagination.total} conversations
        </div>

        {/* Modal for Create/Edit Conversation */}
        {showModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>
                  {editingConversation ? 'Edit Conversation' : 'Create Conversation'}
                </h3>
                <button className="close-btn" onClick={handleCancel}>×</button>
              </div>

              <form onSubmit={handleSubmit} className="conversation-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>User ID *</label>
                    <input
                      type="number"
                      name="userId"
                      value={formData.userId}
                      onChange={handleInputChange}
                      placeholder="Enter User ID"
                      min="1"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Language *</label>
                    <select
                      name="language"
                      value={formData.language}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="en">English</option>
                      <option value="ms">Malay</option>
                      <option value="zh">Chinese</option>
                      <option value="ta">Tamil</option>
                    </select>
                  </div>

                  <div className="form-group full-width">
                    <label>Title *</label>
                    <input
                      type="text"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter conversation title"
                      maxLength="255"
                      required
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleCancel} className="cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" className="submit-btn">
                    {editingConversation ? 'Update' : 'Create'} Conversation
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Chat;